import React, { useEffect, useMemo, useRef, useState } from 'react';
import { draftToMarkdown, markdownToDraft } from 'markdown-draft-js';
import {
	EditorState,
	DraftHandleValue,
	convertToRaw,
	convertFromRaw,
	RichUtils,
	CompositeDecorator,
	ContentBlock,
	ContentState
} from 'draft-js';
import Editor from 'draft-js-plugins-editor';
import createImagePlugin from 'draft-js-image-plugin';
import 'draft-js/dist/Draft.css';
import classNames from 'classnames';

import Icon, { ICONS, COLORS } from '../icon';
import SelectNative from '../select/select-native';
import EditSectionColorsModal, { EContentColorOverrideNames } from '../edit-section-colors/edit-section-colors-modal';
import { ColorKeys, TextOverrides } from '../../../types/working-model';
import { useTypedSelector } from '../../../store/reducers/use-typed-selector';
import TextInput, { Validation } from "../text-input/text";
import { validateUrl } from "../../../utils/utils";
import './editable-text.scss';
import { parseColor } from '../../../utils/document-head';

const imagePlugin = createImagePlugin();

// options to extend basic conversion functionality
// currently transposes underline in draft to strikethrough in markdown since markdown doesn't support underline
// also preserves newlines so multiple linebreaks are not ignored
const customToMarkdown = {
	styleItems: {
		UNDERLINE: {
			open: () => '~~',
			close: () => '~~',
		}
	},
	preserveNewlines: true
};
const customToDraft = {
	blockStyles: {
		'del_open': 'UNDERLINE',
	},
	remarkableOptions: {
		enable: {
			inline: "del",
		},
	},
	preserveNewlines: true
};

// link decorator functions directly from example: https://github.com/facebook/draft-js/blob/main/examples/draft-0-10-0/link/link.html
export function findLinkEntities(contentBlock: ContentBlock, callback: (start: number, end: number) => void, contentState: ContentState): void {
	contentBlock.findEntityRanges(
		(character: any) => {
			const entityKey = character.getEntity();
			return (
				entityKey !== null &&
				contentState.getEntity(entityKey).getType() === 'LINK'
			);
		},
		callback
	);
}

interface IEditableTextLinkProps {
	contentState: ContentState;
	entityKey: string;
	children: JSX.Element
}

export const Link: React.FC<IEditableTextLinkProps> = (props: IEditableTextLinkProps) => {
	const { url } = props.contentState.getEntity(props.entityKey).getData();
	return (
		<a href={url} target="_blank" rel="noreferrer" >
			{props.children}
		</a>
	);
};
const decorator = new CompositeDecorator([{ strategy: findLinkEntities, component: Link, }]);


interface EditableTextDraftProps {
	value: string;
	placeholder?: string;
	className?: string;
	onChange: (text: string) => void;
	style?: React.CSSProperties;
	elementName?: EContentColorOverrideNames;
	onOverrideChange?: (override: TextOverrides) => void;
	styleOverrides?: TextOverrides;
	defaultColor?: ColorKeys;
	disabled?: boolean;
}

const EditableTextDraft: React.FC<EditableTextDraftProps> = (props: EditableTextDraftProps) => {
	const {
		styleOverrides = {},
		onOverrideChange,
		defaultColor,
		elementName,
		value,
		className,
		style,
		onChange,
		placeholder,
		disabled
	} = props;

	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const currentPostRegisterPage = useTypedSelector(state => state.CreateEventReducer.currentPostRegisterPage);

	const editor = useRef<any>(null);
	const currentContent = useRef<any>(null);
	const [editorState, setEditorState] = useState(
		value
			? EditorState.createWithContent(convertFromRaw(markdownToDraft(value as string, customToDraft)), decorator)
			: EditorState.createEmpty(decorator)
	);
	const [active, setActive] = useState(false);
	const [activeStates, setActiveStates] = useState<string[]>([]);
	const [activeBlockType, setActiveBlockType] = useState('paragraph');
	const colorPalette = workingEvent?.settings.design.colors.colors;
	const color = styleOverrides.color?.split("-")[0];
	const [colorsOpen, setColorsOpen] = useState(false);
	const styleClasses: string[] = Object.values(styleOverrides);
	const [lineHeight, setLineHeight] = useState<string>(styleOverrides.lineHeight || '');
	const [linkEditOpen, setLinkEditOpen] = useState<boolean>(false);
	const [link, setLink] = useState<string>("");

	function focusEditor() {
		editor.current?.focus();
	}

	useEffect(() => {
		if (value) {
			setEditorState(EditorState.createWithContent(convertFromRaw(markdownToDraft(value as string, customToDraft))));
		}
	}, [value]);

	useEffect(() => {
		currentContent.current = editorState;
	}, [editorState]);

	function onChangeEditor(editorState: EditorState) {
		const activeStates = Array.from(editorState.getCurrentInlineStyle().values() as any) as string[];
		setActiveStates(activeStates as string[]);
		setEditorState(editorState);

		const currentContent = editorState.getCurrentContent();
		const currentBlock = currentContent.getBlockForKey(editorState.getSelection().getFocusKey());
		setActiveBlockType(currentBlock.getType());

		const linkKey = currentBlock.getEntityAt(editorState.getSelection().getStartOffset());
		setLink(!linkKey ? "" : currentContent.getEntity(linkKey).getData().url);
	}

	function undo(e: any) {
		e.preventDefault();
		setEditorState(EditorState.undo(editorState));
	}

	function redo() {
		setEditorState(EditorState.redo(editorState));
	}

	function toggleSize(type: string) {
		setTimeout(focusEditor, 16);
		onChangeEditor(RichUtils.toggleBlockType(editorState, type));
	}

	function toggleUL(e: any): DraftHandleValue {
		e.preventDefault();
		const newState = RichUtils.toggleBlockType(editorState, 'unordered-list-item');

		if (newState) {
			setEditorState(newState);
			return 'handled';
		}

		return 'not-handled';
	}

	function toggleOL(e: any): DraftHandleValue {
		e.preventDefault();
		const newState = RichUtils.toggleBlockType(editorState, 'ordered-list-item');

		if (newState) {
			setEditorState(newState);
			return 'handled';
		}

		return 'not-handled';
	}

	const handleLineHeight = (lineHeight: string) => {
		setLineHeight(lineHeight);
		if (!onOverrideChange) return;
		onOverrideChange({ ...styleOverrides, lineHeight });
	};

	function handleFontStyle(e: any, style: number | string): DraftHandleValue {
		//cmd + B/I/U will send a string as e instead of click event
		if (typeof e !== "string") {
			e.preventDefault();
		}

		const newState = RichUtils.toggleInlineStyle(editorState, String(style));

		if (activeStates.includes(String(style))) {
			setActiveStates(activeStates.filter((st: string) => st !== String(style)));
		}

		if (newState) {
			setEditorState(newState);
			setTimeout(focusEditor, 16);
			return 'handled';
		}

		return 'not-handled';
	}

	function handleColorChange(color: string) {
		if (!onOverrideChange) return;
		onOverrideChange({ ...styleOverrides, color });
	}

	const startingColor = color || defaultColor;
	const currentColor: [string, number] | undefined = useMemo(() => startingColor ? colorPalette?.[startingColor] : ['#444', 1], [startingColor, colorPalette]);

	const validLink = useMemo((): boolean => {
		if (link.length === 0) return true;
		return validateUrl(link.trim());
	}, [link]);

	const handleLink = (e: any): void => {
		// prevent default keeps the text highlighted when clicking the link button
		// I can't figure out how to keep it highlighted when clicking into the url input
		e.preventDefault();
		setLinkEditOpen(currentState => !currentState);
	};

	const handleSaveLink = (remove?: boolean): DraftHandleValue => {
		let newState;
		if (remove || link.length === 0) {
			newState = RichUtils.toggleLink(editorState, editorState.getSelection(), null);
		} else {
			const contentState = editorState.getCurrentContent();
			const contentStateWithEntity = contentState.createEntity('LINK', 'MUTABLE', { url: link.trim() });
			const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
			const editorStateWithEntity = EditorState.set(editorState, { currentContent: contentStateWithEntity });
			newState = RichUtils.toggleLink(editorStateWithEntity, editorStateWithEntity.getSelection(), entityKey);
		}

		if (newState) {
			setEditorState(newState);
			setLinkEditOpen(false);
			setLink("");
			return 'handled';
		}
		return 'not-handled';
	};

	useEffect(() => {
		function checkClick(e: any) {
			const path = e.path || e.composedPath();
			for (const item of path) {
				if (item.id === 'editable-text') {
					return;
				}
			}
			const markdown = draftToMarkdown(convertToRaw(editorState.getCurrentContent()), customToMarkdown);
			// hack newlines with non-breaking spaces in order to get markdown to recognize multiple line breaks
			const markdownWithBreaks = markdown.split('\n').join('&nbsp; \n');
			onChange(markdownWithBreaks);
			setActive(false);
			setLinkEditOpen(false);
		}

		if (active)
			window.addEventListener('mousedown', checkClick);

		return () => {
			window.removeEventListener('mousedown', checkClick);
		};
	}, [active, editorState, onChange]);

	return (
		<div className={classNames("editable-text", { active, disabled, "post-register": currentPostRegisterPage }, className)} style={style} id="editable-text">
			<div className={classNames(styleClasses)}>
				<Editor
					ref={editor}
					editorState={editorState}
					onChange={onChangeEditor}
					plugins={[imagePlugin]}
					onFocus={() => setActive(true)}
					placeholder={placeholder}
					preserveSelectionOnBlur
				/>
			</div>
			<button className="round edit-text-button" onClick={() => editor.current?.focus()}><Icon name={ICONS.EDIT} size={12} color={COLORS.WHITE} /></button>
			<div className="toolbar">
				<SelectNative
					options={[
						{
							label: "Heading 1",
							value: 'header-one'
						},
						{
							label: "Heading 2",
							value: 'header-two'
						},
						{
							label: "Heading 3",
							value: 'header-three'
						},
						{
							label: "Heading 4",
							value: 'header-four'
						},
						{
							label: "Heading 5",
							value: 'header-five'
						},
						{
							label: "Paragraph",
							value: 'paragraph'
						},
						{
							label: "Unstyled",
							value: 'unstyled'
						}
					]}
					onChange={(e: React.ChangeEvent<HTMLSelectElement>) => toggleSize(e.target.value)}
					value={activeBlockType}
				/>
				{startingColor ? (
					<button
						className={classNames("small round color-display")}
						style={{
							backgroundColor: currentColor ? parseColor(currentColor) : "",
						}}
						onClick={() => setColorsOpen(true)}
					/>
				) : null}
				<div className="editor-controls-container editor-center">
					<button className={classNames("no-cancel bold", { active: activeStates.includes('BOLD') })} onMouseDown={(e) => handleFontStyle(e, 'BOLD')} title="bold"><Icon name="bold" size={20} color={COLORS.MED_LIGHT_GRAY} viewBox={"-3 0 17 14"} /></button>
					<button className={classNames("no-cancel italic", { active: activeStates.includes('ITALIC') })} onMouseDown={(e) => handleFontStyle(e, 'ITALIC')} title="italic"><Icon name="italic" size={20} color={COLORS.MED_LIGHT_GRAY} viewBox={"-4 0 17 14"} /></button>
					<button className={classNames("no-cancel underline", { active: activeStates.includes('UNDERLINE') })} onMouseDown={(e) => handleFontStyle(e, 'UNDERLINE')} title="underline"><Icon name="underline" size={20} color={COLORS.MED_LIGHT_GRAY} viewBox={"-3 -2 17 19"} /></button>
					<button className={classNames("link no-cancel", { active: linkEditOpen })} title="link" onMouseDown={handleLink}><Icon name={ICONS.HYPERLINK} size={20} color={COLORS.MED_LIGHT_GRAY} /></button>
				</div>
				<div className="editor-controls-container editor-center">
					<button className="spacing no-cancel" title="spacing">
						<div className="spacing-options">
							<SelectNative
								options={[
									{
										label: "Default",
										value: ''
									},
									{
										label: "Single",
										value: 'line-height-1'
									},
									{
										label: "1.2",
										value: 'line-height-1_2'
									},
									{
										label: "1.5",
										value: 'line-height-1_5'
									},
									{
										label: "Double",
										value: 'line-height-2'
									},
								]}
								onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleLineHeight(e.target.value)}
								value={lineHeight}
							/>
						</div>
						<Icon name={ICONS.LINE_SPACING} size={20} color={COLORS.MED_LIGHT_GRAY} />
					</button>
					<button className="ol no-cancel" onMouseDown={toggleOL} title="list"><Icon name={ICONS.OL} size={20} color={COLORS.MED_LIGHT_GRAY} /></button>
					<button className="ul no-cancel" onMouseDown={toggleUL} title="bullets"><Icon name={ICONS.UL} size={20} color={COLORS.MED_LIGHT_GRAY} /></button>
				</div>
				<div className="editor-controls-container editor-right">
					<button className="undo no-cancel" onClick={undo}><Icon name={ICONS.UNDO_ALT} color={COLORS.MED_LIGHT_GRAY} size={20} /></button>
					<button className="redo no-cancel" onClick={redo}><Icon name={ICONS.REDO_ALT} color={COLORS.MED_LIGHT_GRAY} size={20} /></button>
				</div>
			</div>

			{linkEditOpen && <div className="toolbar link-editor">
				<span>Enter link: </span>
				<TextInput
					placeholder={"Your url here..."}
					// text input won't display an empty string so we have to pass it a string with a space when link is empty
					value={link ? link : " "}
					onChange={(e) => setLink(e.target.value)}
					valid={!validLink ? Validation.error : Validation.normal}
				/>
				{link && <button className="focus-visible" onClick={() => handleSaveLink(true)}>Remove</button>}
				<button className="primary" disabled={!validLink} onClick={() => handleSaveLink()}>Save</button>
			</div>}

			<EditSectionColorsModal
				open={colorsOpen}
				onClose={() => setColorsOpen(false)}
				onFinish={handleColorChange}
				title={elementName || EContentColorOverrideNames.title}
			/>
		</div>
	);
};

export default EditableTextDraft;
